import { create } from 'zustand'
import {
  PartnerCategory,
  CreatePartnerCategoryRequest,
  UpdatePartnerCategoryRequest
} from '../types/partnerCategory'
import { partnerCategoryService } from '../services/partnerCategoryService'

interface PartnerCategoryState {
  // State
  categories: PartnerCategory[]
  loading: boolean
  error: string | null
  selectedCategory: PartnerCategory | null

  // Actions
  loadCategories: (isActive?: boolean) => Promise<void>
  createCategory: (data: CreatePartnerCategoryRequest) => Promise<PartnerCategory>
  updateCategory: (id: string, data: UpdatePartnerCategoryRequest) => Promise<PartnerCategory>
  setSelectedCategory: (category: PartnerCategory | null) => void
  reset: () => void
}

const usePartnerCategoryStore = create<PartnerCategoryState>((set) => ({
  // Initial State
  categories: [],
  loading: false,
  error: null,
  selectedCategory: null,

  // Actions
  loadCategories: async (isActive?: boolean) => {
    set({ loading: true, error: null })
    try {
      const categories = await partnerCategoryService.getCategories(isActive)
      set({ categories, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load categories',
        loading: false
      })
    }
  },

  createCategory: async (data: CreatePartnerCategoryRequest) => {
    const category = await partnerCategoryService.createCategory(data)

    // Add to local state
    set((state) => ({
      categories: [...state.categories, category].sort((a, b) => a.name.localeCompare(b.name))
    }))

    return category
  },

  updateCategory: async (id: string, data: UpdatePartnerCategoryRequest) => {
    const updated = await partnerCategoryService.updateCategory(id, data)

    // Update local state
    set((state) => ({
      categories: state.categories
        .map((cat) => (cat.id === id ? updated : cat))
        .sort((a, b) => a.name.localeCompare(b.name)),
      selectedCategory: state.selectedCategory?.id === id ? updated : state.selectedCategory
    }))

    return updated
  },

  setSelectedCategory: (category: PartnerCategory | null) => {
    set({ selectedCategory: category })
  },

  reset: () =>
    set({
      categories: [],
      loading: false,
      error: null,
      selectedCategory: null
    })
}))

export default usePartnerCategoryStore
